import React, { useEffect, useState, useRef } from 'react';
import { TransactionApiData } from '../../../shared/types';
import { TransactionApi } from '../../../infrastructure/api';
import { formatCurrency } from '../../../shared/utils';
import { PdfGenerator } from '../../../infrastructure/adapters';

interface LastSalesProps {
    supplierId: number | null;
    token: string | null;
    showLastSales: boolean;
    onToggle: () => void;
    isAlwaysOpen?: boolean;
    onAddXml?: (transactionId: number, file: File) => void;
    xmlAttachedTransactions?: Set<number>;
}

export const LastSales: React.FC<LastSalesProps> = ({
    supplierId,
    token,
    showLastSales,
    onToggle,
    isAlwaysOpen = false,
    onAddXml,
    xmlAttachedTransactions = new Set()
}) => {
    const [transactions, setTransactions] = useState<TransactionApiData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingReceipt, setDownloadingReceipt] = useState<number | null>(null);
    const isInitialLoadRef = useRef<boolean>(true);

    useEffect(() => {
        const loadTransactions = async () => {
            if (!supplierId || !token) {
                if (isInitialLoadRef.current) {
                    setTransactions([]);
                }
                return;
            }

            try {
                setIsLoading(true);
                const apiData = await TransactionApi.fetchBySupplierId(supplierId, token);

                setTransactions(prevTransactions => {
                    if (isInitialLoadRef.current) {
                        isInitialLoadRef.current = false;
                        return apiData;
                    }

                    const existingIds = new Set(prevTransactions.map(t => t.mig_tra_id));
                    const newTransactions = apiData.filter(t => !existingIds.has(t.mig_tra_id));

                    if (newTransactions.length > 0) {
                        return [...newTransactions, ...prevTransactions];
                    }

                    return prevTransactions;
                });
            } catch (error) {
                console.error('Error loading transactions:', error);
                if (isInitialLoadRef.current) {
                    setTransactions([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (showLastSales) {
            loadTransactions();

            const interval = setInterval(() => {
                loadTransactions();
            }, 60000);

            return () => {
                clearInterval(interval);
            };
        } else {
            isInitialLoadRef.current = true;
        }
    }, [supplierId, token, showLastSales]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatValue = (value: string) => {
        return formatCurrency(parseFloat(value));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, transactionId: number) => {
        e.stopPropagation();
        const file = e.target.files?.[0];
        if (file && onAddXml) {
            onAddXml(transactionId, file);
        }
        if (e.target) {
            e.target.value = '';
        }
    };

    const handleDownloadReceipt = async (transaction: TransactionApiData) => {
        try {
            setDownloadingReceipt(transaction.mig_tra_id);
            const receiptText = PdfGenerator.generateReceiptTextFromTransaction(transaction);
            await PdfGenerator.generateReceipt(receiptText);
        } catch (error) {
            console.error('Erro ao baixar comprovante:', error);
        } finally {
            setDownloadingReceipt(null);
        }
    };

    return (
        <div className="last-sales">
            <div
                className={`sales-header-toggle ${isAlwaysOpen ? 'always-open' : ''}`}
                onClick={isAlwaysOpen ? undefined : onToggle}
            >
                <h3>
                    Últimas Vendas
                    {isLoading && !isInitialLoadRef.current && transactions.length > 0 && (
                        <span className="loading-indicator-header">
                            <span className="loading-spinner-small"></span>
                        </span>
                    )}
                </h3>
                <span className={`toggle-icon ${showLastSales ? 'expanded' : ''}`}>
                    ▼
                </span>
            </div>
            {showLastSales && (
                <div className="sales-table">
                    <div className="sales-header">
                        <div className="sales-cell">Autorização</div>
                        <div className="sales-cell">Data</div>
                        <div className="sales-cell">Produto</div>
                        <div className="sales-cell">Valor</div>
                        <div className="sales-cell">Quantidade</div>
                        <div className="sales-cell">Placa</div>
                        <div className="sales-cell">Ações</div>
                    </div>
                    {isLoading && isInitialLoadRef.current && transactions.length === 0 ? (
                        <div className="sales-row">
                            <div className="sales-cell" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                                Carregando...
                            </div>
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="sales-row">
                            <div className="sales-cell" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                                Nenhuma transação encontrada
                            </div>
                        </div>
                    ) : (
                        transactions.map((transaction) => (
                            <div key={transaction.mig_tra_id} className="sales-row">
                                <div className="sales-cell" data-label="Autorização:">{transaction.mig_tra_autorizacao}</div>
                                <div className="sales-cell" data-label="Data:">{transaction.mig_tra_data}</div>
                                <div className="sales-cell" data-label="Produto:">{transaction.mig_pro_produtos}</div>
                                <div className="sales-cell" data-label="Valor:">{formatValue(transaction.mig_tra_valor)}</div>
                                <div className="sales-cell" data-label="Quantidade:">{transaction.mig_tra_quantidade}</div>
                                <div className="sales-cell" data-label="Placa:">{transaction.mig_vei_placa}</div>
                                <div className="sales-cell" data-label="Ações:">
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                            className="receipt-download-button"
                                            onClick={() => handleDownloadReceipt(transaction)}
                                            disabled={downloadingReceipt === transaction.mig_tra_id}
                                            title="Baixar Comprovante"
                                        >
                                            {downloadingReceipt === transaction.mig_tra_id ? '⏳' : '📥'}
                                        </button>
                                        {transaction.xml_anexado === '2' && (
                                            xmlAttachedTransactions.has(transaction.mig_tra_id) ? (
                                                <span className="xml-success-icon" title="XML Anexado">
                                                    ✓
                                                </span>
                                            ) : (
                                                <label className="xml-icon-button" title="Anexar XML">
                                                    📄
                                                    <input
                                                        type="file"
                                                        accept=".xml"
                                                        onChange={(e) => handleFileChange(e, transaction.mig_tra_id)}
                                                        style={{ display: 'none' }}
                                                    />
                                                </label>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};
